import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import { I18nProvider } from "@/i18n";
import { AppShell } from "@/components/layout/AppShell";
import LoginPage from "@/features/auth/LoginPage";
import ForcePasswordChange from "@/features/auth/ForcePasswordChange";
import HomePage from "@/features/home/HomePage";
import CompaniesPage from "@/features/companies/CompaniesPage";
import CompanyDetailPage from "@/features/companies/CompanyDetailPage";
import PropertiesPage from "@/features/properties/PropertiesPage";
import PropertyDetailPage from "@/features/properties/PropertyDetailPage";
import AuditPage from "@/features/audit/AuditPage";
import SettingsPage from "@/features/settings/SettingsPage";
import WorkOrdersPage from "@/features/workorders/WorkOrdersPage";
import WorkOrderDetailPage from "@/features/workorders/WorkOrderDetailPage";
import SchedulePage from "@/features/schedule/SchedulePage";
import ProjectsPage from "@/features/projects/ProjectsPage";
import ProjectDetailPage from "@/features/projects/ProjectDetailPage";
import PublicWorkOrderPage from "@/features/public/PublicWorkOrderPage";
import PublicInvoicePage from "@/features/public/PublicInvoicePage";
import SubsPage from "@/features/subs/SubsPage";
import SubDetailPage from "@/features/subs/SubDetailPage";
import MessagesPage from "@/features/messaging/MessagesPage";
import TowerPage from "@/features/tower/TowerPage";
import IntakePage from "@/features/intake/IntakePage";
import FinancePage from "@/features/finance/FinancePage";
import ReportBuilderPage from "@/features/reports/ReportBuilderPage";
import EstimatesPage from "@/features/estimates/EstimatesPage";
import ExecutiveDashboard from "@/features/dashboards/ExecutiveDashboard";
import FinancialDashboard from "@/features/dashboards/FinancialDashboard";
import OperationalDashboard from "@/features/dashboards/OperationalDashboard";
import CommercialDashboard from "@/features/dashboards/CommercialDashboard";
import ComingSoon from "@/features/placeholder/ComingSoon";

const Splash = () => (
  <div className="flex h-screen items-center justify-center bg-paper text-[13px] text-ink-tertiary">
    Cargando…
  </div>
);

const Protected = ({ children }) => {
  const { session, ready } = useAuth();
  if (!ready) return <Splash />;
  if (!session) return <Navigate to="/login" replace />;
  // Fix I (2026-09-04): con una clave temporal no se entra a ninguna pantalla. Va acá y no
  // en cada ruta porque Protected envuelve toda la app privada: no hay forma de saltearlo.
  if (session?.user?.must_change_password) return <ForcePasswordChange />;
  return children;
};

const Guarded = ({ module, children }) => {
  const { can } = useAuth();
  if (!can(module, "read")) return <Navigate to="/" replace />;
  return children;
};

const LoginRoute = () => {
  const { session, ready } = useAuth();
  if (!ready) return <Splash />;
  if (session) return <Navigate to="/" replace />;
  return <LoginPage />;
};

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrandProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginRoute />} />
              <Route path="/ot/:token" element={<PublicWorkOrderPage />} />
              {/* Fix T (2026-10-05): la factura del cliente tiene su propio link */}
              <Route path="/factura/:token" element={<PublicInvoicePage />} />
              <Route
                element={
                  <Protected>
                    <AppShell />
                  </Protected>
                }
              >
                <Route path="/" element={<HomePage />} />
                <Route
                  path="/companias"
                  element={
                    <Guarded module="companies">
                      <CompaniesPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/companias/:id"
                  element={
                    <Guarded module="companies">
                      <CompanyDetailPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/edificios"
                  element={
                    <Guarded module="properties">
                      <PropertiesPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/edificios/:id"
                  element={
                    <Guarded module="properties">
                      <PropertyDetailPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/auditoria"
                  element={
                    <Guarded module="audit">
                      <AuditPage />
                    </Guarded>
                  }
                />
                <Route path="/configuracion" element={<SettingsPage />} />
                <Route
                  path="/ordenes"
                  element={
                    <Guarded module="work_orders">
                      <WorkOrdersPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/ordenes/:id"
                  element={
                    <Guarded module="work_orders">
                      <WorkOrderDetailPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/programacion"
                  element={
                    <Guarded module="work_orders">
                      <SchedulePage />
                    </Guarded>
                  }
                />
                <Route
                  path="/subcontratistas"
                  element={
                    <Guarded module="subs">
                      <SubsPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/subcontratistas/:id"
                  element={
                    <Guarded module="subs">
                      <SubDetailPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/mensajes"
                  element={
                    <Guarded module="messaging">
                      <MessagesPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/torre-de-control"
                  element={
                    <Guarded module="control_tower">
                      <TowerPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/intake"
                  element={
                    <Guarded module="intake">
                      <IntakePage />
                    </Guarded>
                  }
                />
                <Route
                  path="/proyectos"
                  element={
                    <Guarded module="projects">
                      <ProjectsPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/proyectos/:id"
                  element={
                    <Guarded module="projects">
                      <ProjectDetailPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/finanzas"
                  element={
                    <Guarded module="finance">
                      <FinancePage />
                    </Guarded>
                  }
                />
                <Route
                  path="/reportes"
                  element={
                    <Guarded module="reports">
                      <ReportBuilderPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/estimados"
                  element={
                    <Guarded module="estimates">
                      <EstimatesPage />
                    </Guarded>
                  }
                />
                <Route
                  path="/tablero-ejecutivo"
                  element={
                    <Guarded module="dash_executive">
                      <ExecutiveDashboard />
                    </Guarded>
                  }
                />
                <Route
                  path="/tablero-financiero"
                  element={
                    <Guarded module="dash_financial">
                      <FinancialDashboard />
                    </Guarded>
                  }
                />
                <Route
                  path="/tablero-operativo"
                  element={
                    <Guarded module="dash_operational">
                      <OperationalDashboard />
                    </Guarded>
                  }
                />
                <Route
                  path="/tablero-comercial"
                  element={
                    <Guarded module="dash_commercial">
                      <CommercialDashboard />
                    </Guarded>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <Toaster position="bottom-right" richColors closeButton />
        </BrandProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
